import { useAtom } from 'jotai';
import { RotateCcw } from 'lucide-react';
import { useCallback } from 'react';

import {
  isOnboardingFlowDevOverridesEnabled,
  onboardingFlowDevForceReplayAtom,
} from '@/atoms/onboardingFlowDevAtoms';
import { Button } from '@heidi/design-system/button';
import { Switch } from '@heidi/design-system/switch';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';

const NEW_USER_ROUTE = '/scribe/new-user';

interface OnboardingFlowDevSectionProps {
  /** Called after the user triggers an action that should close the dev-tools sheet. */
  onRequestClose?: () => void;
}

/**
 * Replays the signup personalization flow (Learn Basics / sample conversation /
 * guided tour) on an account that already completed onboarding — normally
 * impossible, because a set `user.country` makes the account a returning user
 * and `/scribe/new-user` redirects straight into the app.
 */
export function OnboardingFlowDevSection({
  onRequestClose,
}: Readonly<OnboardingFlowDevSectionProps>) {
  const [forceReplay, setForceReplay] = useAtom(onboardingFlowDevForceReplayAtom);

  // Full page load, not `router.push`: the eligibility marker and several flags
  // are read once at boot, so a client-side nav would replay the flow with the
  // previous take's flag values.
  const handleRestart = useCallback(() => {
    onRequestClose?.();
    window.location.assign(NEW_USER_ROUTE);
  }, [onRequestClose]);

  if (!isOnboardingFlowDevOverridesEnabled()) {
    return null;
  }

  return (
    <section aria-labelledby="dev-tools-onboarding-flow-heading" className="mb-6">
      <TypographyP2SemiBold id="dev-tools-onboarding-flow-heading" className="mb-2">
        Onboarding personalization flow
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Reopens the signup flow on <span className="font-mono">{NEW_USER_ROUTE}</span> for
        an account that already finished it. Finishing a run really re-submits onboarding
        — it saves the display language and re-runs template creation — so point it at a
        test account, not your own. Clears when this tab closes.
      </TypographyCaption>

      <div className="flex items-center justify-between gap-3">
        <TypographyCaption className="text-text-secondary">
          Force flow to show
        </TypographyCaption>
        <Switch
          checked={forceReplay}
          onCheckedChange={setForceReplay}
          data-testid="dev-tools-onboarding-force-replay"
          aria-label="Force the onboarding personalization flow to show"
        />
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="mt-3 w-full"
        disabled={!forceReplay}
        onClick={handleRestart}
        data-testid="dev-tools-onboarding-restart"
      >
        <RotateCcw size={14} aria-hidden /> Restart flow
      </Button>
    </section>
  );
}
